<template>
  <section class="service-areas-section">
    <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
      <h2 class="section-title">Área de Servicio</h2>
      <p class="section-subtitle">
        Encuentra la clínica más cercana a ti. Tenemos múltiples ubicaciones para servirte mejor.
      </p>
    </div>

    <!-- Cards a la izquierda + Mapa grande a la derecha -->
    <div class="map-and-cards-row">
      <!-- Lista de ubicaciones (izquierda) -->
      <div v-if="locations && locations.length > 0" class="locations-list animate-on-scroll fade-in-up">
        <div
          v-for="(location, index) in locations"
          :key="`location-${index}`"
          class="location-item animate-on-scroll fade-in-up"
          :style="`animation-delay: ${index * 0.05}s`"
        >
          <img
            :src="pinIcon"
            alt="Ubicación"
            class="pin-icon pin-icon-animate"
          />
          <div class="location-item-content">
            <span class="location-item-name">{{ location.name }}</span>
            <span class="location-item-count">{{ locationCenterCount(location) }} centro{{ locationCenterCount(location) !== 1 ? 's' : '' }} médico{{ locationCenterCount(location) !== 1 ? 's' : '' }}</span>
          </div>
        </div>
      </div>

      <!-- Mapa de Texas (derecha, grande) -->
      <div class="texas-map-wrapper animate-on-scroll fade-in-up">
        <img :src="texasMap" alt="Mapa de Texas con ubicaciones" class="texas-map-image" />
      </div>
    </div>

    <!-- Información compartida + Botón Cómo llegar a la derecha -->
    <div class="info-and-directions-row">
      <div
        v-if="sharedInfo"
        class="shared-info-block animate-on-scroll fade-in-up"
      >
        <div class="shared-info-grid">
          <div class="shared-info-item">
            <i class="fas fa-phone shared-info-icon"></i>
            <a :href="`tel:${sharedInfo.phone}`" class="shared-info-link">{{ sharedInfo.phone }}</a>
          </div>
          <div class="shared-info-item">
            <i class="fas fa-envelope shared-info-icon"></i>
            <a :href="`mailto:${sharedInfo.email}`" class="shared-info-link">{{ sharedInfo.email }}</a>
          </div>
          <div class="shared-info-item">
            <i class="fas fa-clock shared-info-icon"></i>
            <span class="shared-info-text">{{ sharedInfo.hours }}</span>
          </div>
          <div class="shared-info-item">
            <i class="fas fa-calendar-alt shared-info-icon"></i>
            <span class="shared-info-text">{{ sharedInfo.days }}</span>
          </div>
        </div>
      </div>

      <div class="directions-btn-wrapper animate-on-scroll fade-in-up">
        <a
          :href="googleMapsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-directions-full"
        >
          <i class="fas fa-directions btn-directions-icon"></i>
          ¿Cómo llegar?
        </a>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ServiceAreasSection',
  props: {
    locations: {
      type: Array,
      required: false,
      default: () => []
    },
    googleMapsUrl: {
      type: String,
      default: 'https://www.google.com/maps'
    }
  },
  data() {
    return {
      pinIcon: require('@/assets/lp/Recurso 34@300xy.png'),
      texasMap: require('@/assets/lp/6.png')
    };
  },
  methods: {
    locationCenterCount(location) {
      return location && (location.centersCount ?? location.count ?? 1) > 0
        ? (location.centersCount ?? location.count ?? 1)
        : 1;
    }
  },
  computed: {
    sharedInfo() {
      if (!this.locations || this.locations.length === 0) return null;
      const first = this.locations[0];
      return {
        phone: first.phone,
        email: first.email,
        hours: first.hours,
        days: first.days
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

@keyframes pin-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

@keyframes pin-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

.service-areas-section {
  padding: 120px 0;
  background: white;
  overflow-x: hidden;

  .section-header {
    margin-bottom: 4rem;
    margin-top: 3rem;
    padding-top: 2rem;

    .section-title {
      font-size: 3rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      letter-spacing: -0.5px;
      line-height: 1.2;
    }

    .section-subtitle {
      font-size: 1.3rem;
      color: $landing-text-medium;
      max-width: 700px;
      margin: 0 auto;
      line-height: 1.7;
    }
  }

  .map-and-cards-row {
    display: flex;
    align-items: flex-start;
    gap: 2rem;
    padding: 2rem 1.5rem;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  .locations-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 0 0 320px;
    min-width: 280px;

    @media (max-width: 991px) {
      flex: 1 1 auto;
      width: 100%;
    }
  }

  .texas-map-wrapper {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;

    .texas-map-image {
      width: 100%;
      min-width: 400px;
      max-width: 100%;
      height: auto;
      object-fit: contain;
    }

    @media (max-width: 991px) {
      .texas-map-image {
        min-width: 100%;
      }
    }
  }

  .location-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: white;
    border-radius: 12px;
    border: 1px solid $landing-border-light;
    box-shadow: 0 2px 12px $landing-primary-rgba-08;
    transition: all 0.3s ease;
    min-width: 0;

    &:hover {
      box-shadow: 0 4px 20px $landing-primary-rgba-12;
      border-color: $landing-primary-color;
    }

    .pin-icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      object-fit: contain;
    }

    .pin-icon-animate {
      animation: pin-float 2.5s ease-in-out infinite;
    }

    &:hover .pin-icon-animate {
      animation: pin-pulse 0.6s ease-in-out;
    }

    .location-item-content {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      min-width: 0;

      .location-item-name {
        font-size: 1.1rem;
        font-weight: 700;
        color: $landing-text-dark;
        line-height: 1.3;
      }

      .location-item-count {
        font-size: 0.9rem;
        color: $landing-text-medium;
        line-height: 1.4;
      }
    }
  }

  .info-and-directions-row {
    display: flex;
    align-items: stretch;
    gap: 2rem;
    padding: 0 1.5rem 2rem;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  .shared-info-block {
    flex: 1;
    padding: 1.5rem;
    background: $landing-bg-light;
    border-radius: 12px;
    border: 1px solid $landing-border-light;

    .shared-info-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem 2rem;

      .shared-info-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .shared-info-icon {
          color: $landing-primary-color;
          font-size: 1rem;
          flex-shrink: 0;
          width: 20px;
          text-align: center;
        }

        .shared-info-link {
          font-size: 1rem;
          color: $landing-primary-color;
          text-decoration: none;
          transition: color 0.2s ease;

          &:hover {
            color: $landing-primary-dark;
            text-decoration: underline;
          }
        }

        .shared-info-text {
          font-size: 1rem;
          color: $landing-text-dark;
          line-height: 1.5;
        }
      }
    }
  }

  .directions-btn-wrapper {
    flex: 0 0 auto;
    display: flex;
    align-items: stretch;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  .btn-directions-full {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    min-width: 200px;
    padding: 16px 28px;
    background: $landing-primary-gradient;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1.15rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 6px 20px $landing-primary-rgba-25;
    letter-spacing: 0.3px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 28px $landing-primary-rgba-35;
      background: $landing-primary-gradient-dark;
      color: white;
      text-decoration: none;
    }

    &:active {
      transform: translateY(0);
    }

    .btn-directions-icon {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 991px) {
    padding: 100px 0;

    .section-header {
      margin-bottom: 3rem;

      .section-title {
        font-size: 2.5rem;
      }

      .section-subtitle {
        font-size: 1.2rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 80px 0;

    .section-header {
      margin-bottom: 3rem;

      .section-title {
        font-size: 2rem;
      }

      .section-subtitle {
        font-size: 1.1rem;
      }
    }

    .shared-info-block .shared-info-grid {
      flex-direction: column;
      gap: 1rem;
    }

    .btn-directions-full {
      padding: 14px 20px;
      font-size: 1.05rem;
    }
  }
}
</style>
